<script>
import { mapActions } from 'vuex';
import i18n from '@/plugins/i18n';

export default {
    props: {
        allDisabled: {
            type: Boolean,
            default: false,
        },

        selectedCompanyId: {
            type: Number,
            required: true
        }
    },

    data() {
        return {
            industryFieldOptions: null,
            currencyOptions: null,

            info: null,
            organizationChart: null,

            updateOrganizationChart: false,
            viewOrganizationChart: false,

            fallbackValue: null,

            isUploadWarningOpen: false,
        }
    },

    computed: {
        showPhoneWarning() {
            if (this.info.company_phone) {
                return this.validatePhoneNumber(this.info.company_phone) == false;
            } else {
                return false;
            }
        }
    },

    mounted() {
        this.setupIndustryAndCurrency();
        this._getSecondSection();
    },

    methods: {
        ...mapActions('auditor', [
            "getSecondSection"
        ]),
        ...mapActions('company', [
            "getSector",
            "getCurrencies",
            "getOrganizationChart",
            "updateCompany",
        ]),
        ...mapActions("reports", ["addOrganizationChart"]),

        setupIndustryAndCurrency() {
            this.getSector({ is_deleted: false })
                .then(res => {
                    this.industryFieldOptions = res.data;
                });

            this.getCurrencies({ is_deleted: false })
                .then(res => {
                    this.currencyOptions = res.data;
                });
        },

        _getSecondSection() {
            this.getSecondSection({ audited_company_id: this.selectedCompanyId })
                .then(res => {
                    this.info = res.data;
                    if (res.data.organization_chart != null && res.data.organization_chart != '') {
                        this._getOrganizationChart(res.data.organization_chart);
                    }
                })
        },

        _getOrganizationChart(directory) {
            const params = {
                organization_chart_link: directory
            }
            this.getOrganizationChart(params)
                .then(res => {
                    const blob = res.data;
                    const fileReader = new FileReader();
                    fileReader.readAsDataURL(blob);
                    fileReader.onloadend = () => {
                        this.organizationChart = fileReader.result;
                    }
                })
                .catch(err => {
                    console.error(err);
                })
        },

        clickSelectFile() {
            if (this.allDisabled) return;
            this.$refs.orgChartInput.click();
            this.setIsUploadWarningOpen(false);
        },

        selectFile(event) {
            if (this.allDisabled) return;
            const organization_chart = event.target.files[0];
            const sizeInKB = organization_chart.size / 1024;
            const maxSizeInKB = 1024;

            if (sizeInKB < maxSizeInKB) {
                this.postOrganizationChart(organization_chart);
            } else {
                this.$notify('error', i18n.t('companyProfile.sizeWarning'), true);
            }
        },

        postOrganizationChart(selectedFile) {
            if (this.allDisabled) return;
            const formData = new FormData();
            formData.append('files', selectedFile);
            formData.append('company_id', this.selectedCompanyId);

            this.addOrganizationChart(formData)
                .then(res => {
                    this._getOrganizationChart(res.data.directory);
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    this.$notify('error', response.data.messageName);
                })
        },

        _updateCompany(event, key) {
            if (this.allDisabled) return;
            const is_valid = this.handleValidations(key);

            if (is_valid == false) {
                event.target.value = this.fallbackValue;
                this.info[`${key}`] = this.fallbackValue;
                this.$notify('error', i18n.t('companyProfile.modal2.check_validity'), true);
                return;
            }

            const params = {
                company_id: this.selectedCompanyId,
                body: {
                    [`${key}`]: event.target.value
                }
            }

            this.updateCompany(params)
                .then(res => {
                    this.$notify('success', res.data.messageName);
                })
                .catch(({ response }) => {
                    event.target.value = this.fallbackValue;
                    event.target.blur();

                    this.info[`${key}`] = this.fallbackValue;

                    this.$notify('error', response.data.messageName);
                })
                .finally(_ => {
                    this.setFallbackValue(null);
                })

        },

        /* setters */

        setIsUploadWarningOpen(val) {
            this.isUploadWarningOpen = val;
        },
        
        setFallbackValue(value) {
            this.fallbackValue = value;
        },

        /* validation */

        handleValidations(key) {
            if (key.includes('phone')) {
                return this.validatePhoneNumber(this.info[`${key}`]);
            } else if (key.includes('email')) {
                return this.validateEmail(this.info[`${key}`]);
            } else if (key === 'total_employee_number') {
                return Number(this.info['total_employee_number']) > 0;
            } else {
                return true;
            }
        },

        validatePhoneNumber(val) {
            if (val && val.length > 0) {
                if (val[0] == '+' && val.length > 6 && val.includes(' ') == false) {
                    return true;
                } else {
                    return false;
                }
            } else {
                return false;
            }
        },

        validateEmail(val) {
            if (val && val.length > 0) {
                if (val.includes('@') && val.includes('.') && val.includes(' ') == false) {
                    if (val.split('@')[1].length >= 3) {
                        return true;
                    } else {
                        return false;
                    }
                } else {
                    return false;
                }
            } else {
                return false;
            }
        },

     

        /* util */

        getSectorName(sector) {
            const lang = localStorage.getItem('lang');
            if (lang == 'en') {
                return sector.sust_sector_name;
            } else if (lang == 'tr') {
                if (sector.sust_sector_name_tr == null) {
                    return sector.sust_sector_name;
                } else {
                    return sector.sust_sector_name_tr;
                }
            } else {
                return sector.sust_sector_name;
            }
        },
    }
}
</script>

<template>
    <div class="second-section">
        <div class="main-row">
            <div class="section-col">
                <div class="section-col-item">
                    <label for="input">
                        {{ $t('companyProfile.modal2.industry') }}
                    </label>
                    <select name="input" v-model="info.sector_id" disabled>
                        <option v-for="sector in industryFieldOptions" :key="sector.sust_sector_id"
                            :value="sector.sust_sector_id"
                        >
                            {{ getSectorName(sector) }}
                        </option>
                    </select>
                </div>

                <div class="section-col-item">
                    <label for="input">
                        {{ $t('companyProfile.modal2.website') }}
                    </label>
                    <input type="text" v-model="info.website" maxlength="100" disabled>
                </div>

                <div class="section-col-item">
                    <label for="input">
                        {{ $t('companyProfile.modal2.sustweb') }}
                    </label>
                    <input type="text" v-model="info.sust_website" maxlength="100" disabled>
                </div>

                <div class="section-col-item">
                    <label for="input" class="has-required-field">
                        {{ $t('companyProfile.modal2.phone') }}

                        <div v-if="showPhoneWarning">
                            {{ $t('companyProfile.modal2.phoneWarning') }}
                        </div>

                        <div v-if="!info.company_phone || info.company_phone.length === 0">
                            {{ $t('companyProfile.requiredWarning') }}
                        </div>
                    </label>
                    <input type="text" v-model="info.company_phone" maxlength="100" disabled>
                </div>
            </div>

            <div class="section-col">
                <div class="section-col-item">
                    <label for="input">
                        {{ $t('companyProfile.modal2.currency') }}
                    </label>
                    <select name="input" v-model="info.currency_id" disabled>
                        <option v-for="currency in currencyOptions" :key="currency.currency_id"
                            :value="currency.currency_id">
                            {{ currency.currency_code }} ({{ currency.currency_symbol }})
                        </option>
                    </select>
                </div>

                <div class="section-col-item">
                    <label for="input">
                        {{ $t('companyProfile.modal2.totalemp') }}
                    </label>
                    <input type="number" v-model="info.total_employee_number" disabled>
                </div>

                <div class="section-col-item">
                    <label for="input">
                        {{ $t('companyProfile.modal2.sustmail') }}
                    </label>
                    <input type="text" v-model="info.sust_email" maxlength="100" disabled>
                </div>

                <div class="section-col-item">
                    <label for="input" class="has-required-field">
                        {{ $t('companyProfile.modal2.email') }}

                        <div v-if="!info.company_email || info.company_email.length === 0">
                            {{ $t('companyProfile.requiredWarning') }}
                        </div>
                    </label>
                    <input type="text" v-model="info.company_email" maxlength="100" disabled>
                </div>
            </div>
        </div>

        <div class="bottom-row">
            <button @click="() => {
                if (organizationChart != null) {
                    this.$refs.viewOrganizationChart.openPopup();
                }
            }">
                {{ $t('companyProfile.modal2.viewOrg2') }}
            </button>
        </div>

        <sust-popup v-if="organizationChart != null" :header="false" :footer="false" size="xl"
            ref="viewOrganizationChart">
            <div class="organization-chart-img-ctr">
                <img class="organization-chart-img" :src="organizationChart" alt="Organization Chart of the company">
            </div>
        </sust-popup>
    </div>
</template>

<style lang='scss' scoped>
.second-section {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    flex-wrap: wrap;
    width: 100%;

    padding: .5em;
}

.main-row {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: flex-start;
    width: 100%;
}

.bottom-row {
    position: relative;

    margin-top: 1em;
    width: 100%;

    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;

    >button {
        background-color: var(--sustainfinity-yellow);
        color: whitesmoke;
        text-transform: uppercase;
        padding: .5em 1em;
        border-radius: .25em;
        box-shadow: 0 .25em .25em .1em gray;

        border: 0;
        outline: 0;

        transition: scale 100ms;

        &:focus {
            outline: thin solid black !important;
        }

        &:active {
            scale: .95;
        }
    }

    >.view-org-chart-text {
        margin-left: 1em;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }
}

.section-col {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    >.section-col-item {
        padding: .5em;
        width: 100%;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;

        >label {
            color: var(--sustainfinity-navy);
            margin: 0 !important;
            padding: 0 !important;
            font-weight: bold;
            font-size: 1.2em;
        }

        >input,
        select {
            padding: .25em;
            border-top: none;
            border-right: none;
            border-left: none;
            border-bottom: thin solid lightgray;
            background-color: whitesmoke;
            width: 100%;

            transition: border-bottom 500ms;

            &:focus {
                border-bottom: thin solid black;
            }

            &:disabled {
                border-radius: .2em;
                background-color: rgb(221, 221, 221);
                cursor: not-allowed;
            }
        }
    }
}

.organization-chart-img-ctr {
    width: 100%;

    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
}

.organization-chart-img {
    height: auto;
    max-width: calc(100% - 15px);
}

.has-required-field {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    > div {
        word-break: normal;
        white-space: wrap;
        padding-left: .5em;
        font-size: .7em;
        color: var(--invalid-input) !important;
    }
}
</style>